import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import QueryProvider from '@jwp/ott-ui-react/src/containers/QueryProvider/QueryProvider';
import { ErrorPageWithoutTranslation } from '@jwp/ott-ui-react/src/components/ErrorPage/ErrorPage';
import LoadingOverlay from '@jwp/ott-ui-react/src/components/LoadingOverlay/LoadingOverlay';
import { AriaAnnouncerProvider } from '@jwp/ott-ui-react/src/containers/AnnouncementProvider/AnnoucementProvider';
import { getSiteType } from '@jwp/ott-ui-react/src/utils/useSiteType';
import Cookies from 'js-cookie';

import initI18n from './i18n/config';
import Root from './containers/Root/Root';

import './screenMapping';
import './styles/main.scss';

interface State {
  isLoading: boolean;
  error?: Error;
}

export default function App() {
  const [i18nState, seti18nState] = useState<State>({ isLoading: true });

  const isProduction = import.meta.env.APP_ENVIRONMENT === 'PRODUCTION';

  useEffect(() => {
    const authDataString = Cookies.get('authData');
    const selectedLibraryString = Cookies.get('SelectedLibraryData');

    if (!authDataString) {
      // window.location.href = 'https://auth.bibliotele.com/';
      return;
    }

    try {
      const authData = JSON.parse(authDataString);
      const { accessToken } = authData;

      if (!accessToken || !accessToken.expires) {
        window.location.href = isProduction ? 'https://auth.bibliotele.com/' : 'https://auth1.bibliotele.com/';
        return;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      if (currentTime > accessToken.expires) {
        window.location.href = isProduction ? 'https://auth.bibliotele.com/' : 'https://auth1.bibliotele.com/';
        return;
      }

      if (selectedLibraryString) {
        const selectedLibraryData = JSON.parse(selectedLibraryString);

        const siteType = getSiteType();
        const libraryType = selectedLibraryData.SelectedLibraryData?.libraryList?.[0]?.libraryParent?.libraryType;
        const tempLibraryType = libraryType === 'academic' ? 'academic' : 'public';

        if (siteType !== tempLibraryType) {
          window.location.href = isProduction ? 'https://auth.bibliotele.com/' : 'https://auth1.bibliotele.com/';
          return;
        }
      } else if (authDataString) {
        const siteType = getSiteType();
        const authData = Cookies.get('authData');
        if (!authData) return;
        const parsedAuthData = JSON.parse(authData);
        const currentTime = Math.floor(Date.now() / 1000);
        if (!(currentTime < parsedAuthData.accessToken.expires)) {
          return;
        }
        const libraryType = parsedAuthData.userData?.metadata?.userLibraryType;
        const tempLibraryType = libraryType === 'academic' ? 'academic' : 'public';
        if (siteType !== tempLibraryType) {
          window.location.href = isProduction ? 'https://auth.bibliotele.com/' : 'https://auth1.bibliotele.com/';
          return;
        }
      }
    } catch (error) {
      console.error('Error parsing auth data:', error);
      window.location.href = isProduction ? 'https://auth.bibliotele.com/' : 'https://auth1.bibliotele.com/';
    }
  }, []);

  useEffect(() => {
    initI18n()
      .then(() => seti18nState({ isLoading: false }))
      .catch((e) => seti18nState({ isLoading: false, error: e as Error }));
  }, []);

  if (i18nState.isLoading) {
    return <LoadingOverlay />;
  }

  if (i18nState.error) {
    // Don't be tempted to translate these strings. If i18n fails to load, translations won't work anyhow
    return (
      <ErrorPageWithoutTranslation
        title={'Unable to load translations'}
        message={'Check your language settings and try again later. If the problem persists contact technical support.'}
        error={i18nState.error}
      />
    );
  }

  return (
    <QueryProvider>
      <BrowserRouter>
        <AriaAnnouncerProvider>
          <Root />
        </AriaAnnouncerProvider>
      </BrowserRouter>
    </QueryProvider>
  );
}
